<template>
</template>

<script>
import _ from 'lodash';
import { Auth, Storage } from 'aws-amplify';
import axios from 'axios';
import ua from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';

import Modes from '../assets/javascripts/misc/Modes';
import FileTypes from '../assets/javascripts/FileTypes';
import Events from '../assets/javascripts/Events';
import Theme from '../assets/javascripts/Theme';
import GHXController from '../assets/javascripts/controllers/GHXController';
import DXFController from '../assets/javascripts/controllers/DXFController';
import PathController from '../assets/javascripts/controllers/PathController';
import PlyController from '../assets/javascripts/controllers/PlyController';
import SBPZip from '../assets/javascripts/sbp/SBPZip';
import Threshold from '../assets/javascripts/ui/Threshold';
import GHValueListObject from '../assets/javascripts/gh/GHValueListObject';
import { converter } from '../assets/javascripts/dxf/DxfConverter';
import CamErrors from '@/assets/jsons/cam_errors.json';

import svgLogo from '@/assets/images/emarf.svg';
import TermsPopup from '@/components/TermsPopup.vue';
import AmplifyLoginPopup from '@/components/AmplifyLoginPopup.vue';
import LoginPopup from '@/components/LoginPopup.vue';
import QRCodePopup from '@/components/QRCodePopup.vue';
import Meatball from '@/components/Meatball.vue';
import BoundingBox from '@/components/viewer/BoundingBox.vue';
import DragToMove from '@/components/viewer/DragToMove.vue';
import LoadingIndicator from '@/components/viewer/LoadingIndicator.vue';
import LeftsideMenu from '@/components/viewer/LeftsideMenu.vue';
import RightsideMenu from '@/components/viewer/RightsideMenu.vue';
import ShopbotMenu from '@/components/viewer/ShopbotMenu.vue';
import Footer from '@/components/viewer/Footer.vue';
import PageBase from './PageBase.vue';

export default {
  name: 'AppBase',
  extends: PageBase,
  components: {
    TermsPopup,
    AmplifyLoginPopup,
    LoginPopup,
    QRCodePopup,
    Meatball,
    Footer,
    BoundingBox,
    DragToMove,
    LoadingIndicator,
    LeftsideMenu,
    RightsideMenu,
    ShopbotMenu
  },

  data () {
    return {
      login: false,
      svg: {
        logo: svgLogo
      },
      mode: Modes.NONE,
      userAgent: 'Unknown',
      controlled: false,
      boundingBox: {
        size: new THREE.Vector3(),
        visible: true
      },
      corners: {
        x: new THREE.Vector2(),
        y: new THREE.Vector2(),
        z: new THREE.Vector2()
      },
      isOpenMobileMenu: false,
      eMaterials: [],
      thresholds: [
        this.createBinThreshold(0)
      ],
      thicknessSlider: new Threshold('板厚（開発用）', 24, 1, 60, 0.5),
      millSize: 6.35,
      machineType: 'Alpha',
      cutSetting: {
        pathHeight: 0,
        xySpeed: 0,
        zSpeed: 0,
        jogSpeed: 0,
      }
    };
  },

  created() {
    this.updateCutSetting()
  },

  mounted () {
    this.baseUrl = this.$router.options.base || '/';
    // eslint-disable-next-line new-cap
    this.userAgent = (new ua()).getOS().name;
    console.log(this.userAgent);
  },

  methods: {
    async setupEMaterials () {
      const url = this.baseUrl + 'static/json/materials.json';
      const { data } = await axios.get(url, { responseType: 'json' });
      this.eMaterials = data.materials;
      return this.eMaterials;
    },

    async setupEPaints () {
      const url = this.baseUrl + 'static/json/paints.json';
      const { data } = await axios.get(url, { responseType: 'json' });
      const { data: { listPaints: { items } } } = data;
      this.ePaints = items;
      return this.ePaints;
    },

    async setupController (type, theme = new Theme()) {
      const canvas = this.$refs.canvas;
      if (this.$options.controller !== undefined) {
        if (this.$options.controller.fileType === type) {
          return this.$options.controller;
        }
        this.$options.controller.destroy();
      }

      const options = {
        unit: 0.5,
        divisions: 20,
        boundingBox: true,
        materials: this.eMaterials,
        paints: this.ePaints
      };
      switch (type) {
        case FileTypes.GHX:
          this.$options.controller = new GHXController(this.baseUrl, canvas, theme, options);
          break;
        case FileTypes.DXF:
          this.$options.controller = new DXFController(this.baseUrl, canvas, theme, options);
          break;
        case FileTypes.PATH:
          this.$options.controller = new PathController(this.baseUrl, canvas, theme, options);
          break;
        case FileTypes.PLY:
          this.$options.controller = new PlyController(this.baseUrl, canvas, theme, options);
          break;
      }
      await this.$options.controller.setupAsync();

      this.$options.controller.on(Events.UPDATE, () => {
        this.resetSbp();
        this.alignBoundingBox();
      }).on(Events.UPDATE_CONTROLS, () => {
        this.alignBoundingBox();
        this.controlled = true;
      });
      this.$options.controller.resize();

      // this.cnc = this.$options.controller.getCNCVisibled();
      this.cnc = false; // set OFF as default to relieve loading

      // this doesn't work because "sbpVisualizers" is not ready yet
      // this.$options.controller.setBinVisibled(false);

      return this.$options.controller;
    },

    requireAmplifyLogin () {
      // amplifyログインしていない場合はログインフォームを表示する
      return Auth.currentAuthenticatedUser().then(() => {
        this.login = true;
      }).catch(() => {
        this.login = false;
        this.$refs.AmplifyLoginPopup.show();
      });
    },

    createBinThreshold (idx, width, depth) {
      const valueX = width || 1820;
      const valueY = depth || 910;
      return {
        title: `#${idx + 1} board`,
        x: new Threshold('board width', valueX, 100, 2440),
        y: new Threshold('board depth', valueY, 100, 1220)
      };
    },

    alignBinThresholds (count) {
      const binCount = Math.max(1, Math.floor(count / 2));
      const diff = binCount - this.thresholds.length;
      if (diff >= 0) {
        // add new threshold
        for (let i = 0; i < diff; i++) {
          this.thresholds.push(this.createBinThreshold(this.thresholds.length));
        }
      } else {
        // remove
        for (let i = Math.abs(diff) - 1; i >= 0; i--) {
          this.thresholds.pop();
        }
      }
    },

    extractJSONPaths (paths, bins, materialId, thickness, nesting) {
      const { millSize, cutSetting } = this;
      const { pathHeight, xySpeed, zSpeed, jogSpeed } = cutSetting;
      return {
        paths,
        bins,
        thickness,
        nesting,
        material: {
          id: materialId,
          price: 1000,
          path_height: pathHeight || millSize,
          xy_speed: xySpeed || 75,
          z_speed: zSpeed || 25,
          jog_speed: jogSpeed || 304.8,
          rotation_speed: 18000
        },
        mill: millSize,
        cut_sheet: false
      };
    },

    getSbpPathKey (path) {
      return `material_id:${path.material_id},paint_id:${path.paint_id},thickness:${path.thickness}`;
    },

    groupSbpComponents (components) {
      const groups = {};
      components.forEach(component => {
        component.paths.forEach(path => {
          const key = this.getSbpPathKey(path);
          if (!(key in groups)) {
            groups[key] = [];
          }
          groups[key].push(path);
        });
      });
      return groups;
    },

    async requestSbp (paths, bins, materialId, paintId, thickness, nesting = true) {
      const json = this.extractJSONPaths(paths, bins, materialId, thickness, nesting);
      const { data: response } = await axios.post(
        `${this.apiRoot}/shopbot`, json, { headers: { 'Content-Type': 'application/json' } }
      );

      console.log(response.version, response.cost);

      if (response.zip !== undefined) {
        const files = await this.unzip(response.zip);
        return {
          materialId, paintId, files, response
        };
      }
      throw response;
    },

    hasMaterialId (paths) {
      const path = paths[0];
      return path.material_id !== undefined;
    },

    extractMaterialId (paths) {
      const path = paths[0];
      return path.material_id !== undefined ? path.material_id : 1;
    },

    extractPaintId (paths) {
      const path = paths[0];
      return path.paint_id !== undefined ? path.paint_id : 1;
    },

    extractThickness (paths, materialId) {
      const path = paths[0];
      if (path.thickness !== undefined) {
        return path.thickness;
      }

      const uis = this.UIs();
      const thicknessItem = uis.find(ui => (ui.nickName.indexOf("板厚") >= 0));
      if (thicknessItem instanceof GHValueListObject) {
        const value = thicknessItem.values[thicknessItem.selected];
        return Number(value);
      }

      // const material = this.eMaterials.find(m => m.id === materialId);
      // if (material) {
      //   return material.thickness;
      // }

      return this.thicknessSlider.value;
    },

    requestSbps (components) {
      const groups = this.groupSbpComponents(components);
      const nesting = components.some(c => (c.nesting === undefined) || c.nesting);
      const promises = Object.keys(groups).map(key => {
        const paths = groups[key];
        const materialId = this.extractMaterialId(paths);
        const paintId = this.extractPaintId(paths);
        const thickness = this.extractThickness(paths, materialId);
        let bins = [];
        // const m = this.eMaterials.find(m => m.id === materialId);
        // if (this.hasMaterialId(paths) && m !== undefined) {
        //   bins.push({
        //     width: m.width,
        //     depth: m.height
        //   });
        // }
        bins = this.thresholds.map(threshold => {
          return {
            width: threshold.x.value,
            depth: threshold.y.value
          };
        });
        return this.requestSbp(paths, bins, materialId, paintId, thickness, nesting);
      });

      return Promise.all(promises).then((result) => {
        this.sbp = new SBPZip();
        const files = result.map(el => {
          this.sbp.add(el.response.zip);
          const { materialId, paintId } = el;
          return el.files.map(file => {
            return { ...file, materialId, paintId };
          });
        }).flat(1);
        this.$options.controller.addSbp(files);
        this.$options.controller.hideSbp();

        const maxFilesCount = Math.max(...result.map(el => el.files.length));
        this.alignBinThresholds(maxFilesCount);
      });
    },

    clearSbp () {
      this.$options.controller.resetSbp();
      this.sbp = null;
    },

    resetSbp () {
      this.clearSbp();
      this.hideSbp();
    },

    showSbp () {
      this.$options.controller.showSbp();
      this.alignBoundingBox();
    },

    hideSbp () {
      this.$options.controller.hideSbp();
      this.syncBoundingBox();
    },

    resetGUI () {
      this.resetSbp();
      this.$options.controller.UIs.forEach(ui => {
        ui.on(Events.UI_CHANGE, () => {
          this.$options.controller.update(ui);
        });
      });
      this.alignBoundingBox();
    },

    alignBoundingBox () {
      // align object bounding box
      const corners = this.$options.controller.boundingBox.getWorldCorners();
      this.corners.x.copy(this.$options.controller.worldToScreen(corners.x));
      this.corners.y.copy(this.$options.controller.worldToScreen(corners.y));
      this.corners.z.copy(this.$options.controller.worldToScreen(corners.z));
      this.boundingBox.size.copy(this.$options.controller.boundingBox.size);
    },

    changeMode (m) {
      this.mode = m;
    },

    changeMillSize (evt) {
      this.millSize = parseFloat(evt.target.value);
      this.updateCutSetting();
    },

    changeMachineType(evt) {
      this.machineType = evt.target.value;
      this.updateCutSetting();
    },

    async parse (type, data, file, name) {
      await this.setupController(type);
      this.$options.controller.parse(data, this.report);
      this.resetGUI();
    },

    async onToggleSbp () {
      switch (this.mode) {
        case Modes.NONE: {
          if (this.sbp !== null) {
            this.showSbp();
          } else {
            await this.generateToolpath();
          }
          this.changeMode(Modes.TOOLPATH);
          break;
        }
        case Modes.TOOLPATH: {
          this.hideSbp();
          this.changeMode(Modes.NONE);
          break;
        }
      }
    },

    onDownloadDXF () {
      const components = this.exportComponents();
      const dxf = converter(components);
      const url = URL.createObjectURL(new Blob([dxf], { type: 'text/dxf' }));
      const link = document.createElement('a');
      link.href = url;
      if (this.sbp !== null) {
        link.setAttribute('download', `${this.sbp.name}.dxf`);
      } else {
        link.setAttribute('download', 'emarf.dxf');
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    onCutOrder () {
      this.authBefore(this.uploadPathJSONs);
    },

    authBefore (callback) {
      Auth.currentAuthenticatedUser().then(() => {
        try {
          this.isLoading = true;
          callback();
        } finally {
          this.isLoading = false;
        }
      }).catch(() => {
        this.$refs.LoginPopup.show();
      });
    },

    uploadPathJSONs () {
      const components = this.exportComponents();
      const groups = this.groupSbpComponents(components);
      const nesting = components.some(c => (c.nesting === undefined) || c.nesting);

      Object.keys(groups).forEach(key => {
        const paths = groups[key];
        const materialId = this.extractMaterialId(paths);
        const thickness = this.extractThickness(paths, materialId);
        const material = this.eMaterials.find(m => (m.id === materialId));

        let bins = [];
        if (this.hasMaterialId(paths) && material !== undefined) {
          bins.push({
            width: material.width,
            depth: material.height
          });
        } else {
          bins = this.thresholds.map(threshold => {
            return {
              width: threshold.x.value,
              depth: threshold.y.value
            };
          });
        }

        const pathJSON = this.extractJSONPaths(
          paths, bins, materialId, thickness, nesting
        );
        const id = uuidv4();
        Storage.put(`${id}.json`, pathJSON).then(result => {
          let { key } = result;
          key = key.replace('.json', '');
          const url = this.makePreviewURL(`/preview/?path=${key}`);
          window.open(url, '_blank');
        });
      });
    },

    makePreviewURL(url) {
      if (this.$emarf.isViewer) {
        return `https://emarf.co${url}`;
      }
      return url;
    },

    generateToolpath () {
      this.isLoading = true;
      this.resetSbp();
      return this.requestSbps(this.exportComponents()).then(this.showSbp).catch((e) => {
        this.clearSbp();
        if (e.response && e.response.data) {
          const { data } = e.response;
          const errors = (Array.isArray(data.errors) ? data.errors : []).map(error => {
            return { ...error, message: CamErrors[error.ecode] };
          });
          this.displayAlert(data.message, false, errors);
        }
        else if (e.message !== undefined) {
          this.displayAlert(e.message, e.contact, e.errors);
        }
        else {
          this.displayAlert('Cutting code generation failed', false, e.errors);
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },

    syncBoundingBox () {
      this.$options.controller.boundingBox.visible = this.boundingBox.visible;
    },

    UIs () {
      return (this.$options.controller !== undefined) ? this.$options.controller.UIs : [];
    },

    exportComponents () {
      return (this.$options.controller !== undefined) ? this.$options.controller.exportComponents : [];
    },

    updateCutSetting() {
      if (this.machineType === "Alpha") {
        this.cutSetting.pathHeight = this.millSize;
        this.cutSetting.xySpeed = 75;
        this.cutSetting.jogSpeed = 304.8;

        this.thresholds.forEach(threshold => {
          threshold.x.value = 1820;
          threshold.y.value = 910;
        });
      }
      else if (this.machineType === "DesktopMAX") {
        this.cutSetting.pathHeight = this.millSize === 6.35 ? 6.35 : 8;
        this.cutSetting.xySpeed = 50;
        this.cutSetting.jogSpeed = 100;

        this.thresholds.forEach(threshold => {
          threshold.x.value = 965;
          threshold.y.value = 635;
        });
      }
    },
  }
};

</script>



// WEBPACK FOOTER //
// src/pages/AppBase.vue